/* Pulse Mode monitor — a live flow graph, like a heart monitor.
   Continuous: steady ~661 mL/min. Pulse Mode: gas stored on the exhale is released on the inhale,
   peaking at up to 1,322 mL/min. Per-breath figures from the H₂HUBB report (44–60 mL per synced breath). */
(() => {
  const T = (x) => (window.NORD_T && window.NORD_T[x]) || x;
  const root = document.querySelector("[data-pulse]");
  if (!root) return;
  const cv = root.querySelector("canvas"), ctx = cv.getContext("2d");
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const lungs = root.querySelector("[data-lungs]"), cue = root.querySelector("[data-cue]");
  const perBreath = root.querySelector("[data-per-breath]"), flowNow = root.querySelector("[data-flow-now]");
  const btns = [...root.querySelectorAll("[data-mode]")];

  const BREATH = 5, INHALE = 2, BASE = 661, PEAK = 1322, WINDOW = 15; // seconds visible
  let pulseOn = true, userPicked = false, t = 0, last = 0, W = 0, H = 0;

  const phase = (tt) => ((tt % BREATH) + BREATH) % BREATH;
  const inhaling = (tt) => phase(tt) < INHALE;
  // flow to the cannula at time tt (mL/min)
  const flow = (tt, on) => {
    if (!on) return BASE;
    const p = phase(tt);
    if (p < INHALE) { const k = Math.sin(Math.PI * Math.min(1, p / (INHALE * 0.9))); return BASE + (PEAK - BASE) * Math.max(0, k); }
    return BASE; // production continues in the background; stored gas is released on the next inhale
  };
  const size = () => {
    const d = Math.min(2, devicePixelRatio || 1), r = cv.getBoundingClientRect();
    W = r.width; H = r.height; cv.width = Math.round(W * d); cv.height = Math.round(H * d); ctx.setTransform(d, 0, 0, d, 0, 0);
  };
  const pad = { l: 54, r: 16, t: 26, b: 30 };
  const y = (v) => pad.t + (H - pad.t - pad.b) * (1 - v / 1450);

  const draw = () => {
    ctx.clearRect(0, 0, W, H);
    const x0 = pad.l, x1 = W - pad.r, pw = x1 - x0, now = t;
    ctx.font = "500 10px 'JetBrains Mono', monospace"; ctx.textAlign = "right"; ctx.textBaseline = "middle";
    [[0, 0], [BASE, 661], [PEAK, 1322]].map(([v, n]) => [v, n.toLocaleString(document.documentElement.lang || "en-US")]).forEach(([v, l], i) => {
      ctx.strokeStyle = i === 0 ? "rgba(207,220,255,.25)" : "rgba(207,220,255,.10)"; ctx.setLineDash(i ? [3, 5] : []);
      ctx.beginPath(); ctx.moveTo(x0, y(v)); ctx.lineTo(x1, y(v)); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = "rgba(207,220,255,.55)"; ctx.fillText(l, x0 - 8, y(v));
    });
    ctx.save(); ctx.translate(12, (pad.t + H - pad.b) / 2); ctx.rotate(-Math.PI / 2); ctx.textAlign = "center";
    ctx.fillStyle = "rgba(207,220,255,.45)"; ctx.fillText(T("H₂ FLOW · mL/min"), 0, 0); ctx.restore();
    const bars = W < 560 ? 36 : 60, bw = pw / bars;
    for (let i = 0; i < bars; i++) {
      const tt = now - WINDOW + (i + 0.5) * (WINDOW / bars);
      const v = flow(tt, pulseOn), xb = x0 + i * bw;
      const isPulse = pulseOn && inhaling(tt), lost = !pulseOn && !inhaling(tt);
      const grad = ctx.createLinearGradient(0, y(v), 0, y(0));
      if (isPulse) { grad.addColorStop(0, "rgba(127,162,255,1)"); grad.addColorStop(1, "rgba(51,79,180,.85)"); }
      else if (lost) { grad.addColorStop(0, "rgba(150,160,185,.28)"); grad.addColorStop(1, "rgba(150,160,185,.08)"); }
      else { grad.addColorStop(0, "rgba(168,196,255,.55)"); grad.addColorStop(1, "rgba(168,196,255,.18)"); }
      ctx.fillStyle = grad;
      const bx = xb + bw * 0.14, bwid = bw * 0.72, top = y(v);
      ctx.beginPath(); ctx.roundRect(bx, top, bwid, y(0) - top, [3, 3, 0, 0]); ctx.fill();
      if (isPulse && v > PEAK * 0.97) { ctx.shadowColor = "rgba(127,162,255,.9)"; ctx.shadowBlur = 18; ctx.fillStyle = "#cfdcff"; ctx.fillRect(bx, top, bwid, 3); ctx.shadowBlur = 0; }
    }
    if (pulseOn) {
      ctx.textAlign = "center"; ctx.textBaseline = "bottom"; ctx.font = "700 11px 'JetBrains Mono', monospace";
      const first = Math.ceil((now - WINDOW) / BREATH) * BREATH;
      for (let b = first; b <= now; b += BREATH) {
        const xp = x0 + ((b + INHALE * 0.45) - (now - WINDOW)) / WINDOW * pw;
        if (xp > x0 + 20 && xp < x1 - 10) { ctx.fillStyle = "#7fa2ff"; ctx.fillText(T("PULSE"), xp, y(PEAK) - 6); }
      }
    }
    if (!pulseOn) {
      ctx.textAlign = "center"; ctx.textBaseline = "bottom"; ctx.font = "700 10px 'JetBrains Mono', monospace";
      const first = Math.ceil((now - WINDOW) / BREATH) * BREATH;
      for (let b = first; b <= now; b += BREATH) {
        const xp = x0 + ((b + INHALE + (BREATH - INHALE) / 2) - (now - WINDOW)) / WINDOW * pw;
        if (xp > x0 + 20 && xp < x1 - 10) { ctx.fillStyle = "rgba(255,138,112,.9)"; ctx.fillText(T("LOST ON EXHALE"), xp, y(BASE) - 6); }
      }
    }
    ctx.strokeStyle = "rgba(255,255,255,.35)"; ctx.beginPath(); ctx.moveTo(x1, pad.t - 8); ctx.lineTo(x1, y(0)); ctx.stroke();
    ctx.textAlign = "center"; ctx.textBaseline = "top"; ctx.font = "500 10px 'JetBrains Mono', monospace"; ctx.fillStyle = "rgba(207,220,255,.4)";
    ctx.fillText(T("15 s ago"), x0 + 30, y(0) + 10); ctx.fillText(T("now"), x1 - 12, y(0) + 10);
    const p = phase(now), inh = p < INHALE;
    const fill = inh ? 0.5 - 0.5 * Math.cos(Math.PI * p / INHALE) : 0.5 + 0.5 * Math.cos(Math.PI * (p - INHALE) / (BREATH - INHALE));
    if (lungs) { lungs.style.setProperty("--s", (0.62 + fill * 0.38).toFixed(3)); lungs.classList.toggle("is-pulse", pulseOn && inh); }
    if (cue) { const c = inh ? (pulseOn ? T("Breathe in · PULSE") : T("Breathe in")) : (pulseOn ? T("Breathe out · storing H₂") : T("Breathe out · H₂ lost")); if (cue.textContent !== c) cue.textContent = c; }
    if (flowNow) flowNow.textContent = Math.round(flow(now, pulseOn)).toLocaleString(document.documentElement.lang || "en-US");
    if (perBreath) { const s = pulseOn ? "44–60" : "≈ 22"; if (perBreath.textContent !== s) perBreath.textContent = s; }
  };

  const setMode = (on, byUser) => {
    pulseOn = on; if (byUser) userPicked = true;
    btns.forEach((b) => b.setAttribute("aria-pressed", String((b.dataset.mode === "pulse") === on)));
    root.classList.toggle("is-off", !on);
  };
  btns.forEach((b) => b.addEventListener("click", () => setMode(b.dataset.mode === "pulse", true)));

  size(); addEventListener("resize", () => { size(); draw(); });
  t = WINDOW + BREATH * 2;
  if (reduced) { t += INHALE * 0.5; draw(); return; }
  let running = false, raf = 0, lastBreath = -1;
  const loop = (ms) => {
    const dt = Math.min(0.05, (ms - last) / 1000 || 0.016); last = ms; t += dt;
    const bi = Math.floor(t / BREATH);
    if (!userPicked && bi !== lastBreath) { lastBreath = bi; if (bi % 6 === 0) setMode(true); else if (bi % 6 === 4) setMode(false); }
    draw(); if (running) raf = requestAnimationFrame(loop);
  };
  new IntersectionObserver(([e]) => {
    if (e.isIntersecting && !running) { running = true; last = performance.now(); raf = requestAnimationFrame(loop); }
    else if (!e.isIntersecting) { running = false; cancelAnimationFrame(raf); }
  }, { threshold: 0.1 }).observe(cv);
  draw();
})();
